import { useStateWithCallbackLazy } from 'use-state-with-callback';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setUnit } from '../../Session';
import ExpandableGeneric from '../ExpandableGeneric';
import CompletionButton from '../CompletionBreakdown/CompletionButton';
import { ElementaryLessonWithStandards } from '../../CurriculumElementaryDashboard/elementaryCompletionHelpers';
import { LessonWithStandards } from '../../CurriculumMiddleSchoolDashboard/completionHelpers';
import { secondarySchoolLessonNumberLookup } from '../../GlobalFunctions';
import styles from './StandardCard.module.scss';
import { ClassroomType } from '../../../peekapak-types/DataProtocolTypes';

interface Props {
  name: string;
  description: string;
  lessons: ElementaryLessonWithStandards[] | LessonWithStandards[];
  isComplete?: boolean;
  classroom: ClassroomType;
  passExpandToParent: () => void;
}

const StandardCard = ({
  name,
  description,
  lessons,
  isComplete,
  classroom,
  passExpandToParent,
}: Props): JSX.Element => {
  const [isExpanded, setIsExpanded] = useStateWithCallbackLazy(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const isHighSchool = classroom.grade > 8;
  const handleClick = () => {
    setIsExpanded((prevState) => !prevState, passExpandToParent);
  };
  const goToLesson = (unitId: string, lessonId: string) => {
    history.push(
      `/lessonPlan/${unitId}/${classroom.language}/${classroom.grade}/lesson/${lessonId}/activity/unspecified`,
    );
  };

  const goToElementaryLesson = (unitId: string, url: string) => {
    dispatch(setUnit(unitId));
    history.push(url);
  };

  return (
    <div className={styles.container} style={{ paddingBottom: isExpanded ? '1em' : 0 }}>
      <div className={styles.standardInfo}>
        <h3 className={styles.title}>{name}</h3>
        <p>{description}</p>
        <div className={styles.expandButton} onClick={handleClick} role='button'>
          {isExpanded ? 'Hide ' : 'Show '}
          <br /> Lessons
        </div>
      </div>
      <ExpandableGeneric isExpanded={isExpanded}>
        {lessons.map((lesson, i) => {
          const { unitId, lessonId, displayName, unitNum } = lesson;
          return (
            <div className={styles.lessonExpandable} key={`${unitId}${lessonId}${i}`}>
              <div className={styles.unitLesson}>
                <p>Unit {unitNum}</p>
                <p>{!lesson.title && `Lesson ${secondarySchoolLessonNumberLookup(lessonId, isHighSchool)}`}</p>
              </div>
              <h4>
                {displayName}: {lesson.title ?? lessonId}
              </h4>

              <div className={styles.buttonContainer}>
                <CompletionButton
                  handleClick={
                    lesson?.url ? () => goToElementaryLesson(unitId, lesson.url) : () => goToLesson(unitId, lessonId)
                  }
                  buttonText={isComplete ? 'Complete' : 'Explore'}
                />
              </div>
            </div>
          );
        })}
      </ExpandableGeneric>
    </div>
  );
};

export default StandardCard;
